import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import authService from '../../services/auth.service';
import { useLanguage } from '../../context/LanguageContext';

const ROLE_CONFIG = {
  customer: {
    label: 'Customer', icon: '🛍️', color: '#B91C1C',
    subtitle: 'Start shopping on FarmDirectly',
    bg: 'linear-gradient(135deg, #FEF2F2 0%, #FFF7ED 100%)',
    panelBg: 'linear-gradient(180deg, #B91C1C 0%, #991B1B 100%)',
    features: ['🛒 Browse thousands of products', '⭐ Write reviews & earn points', '🎁 Exclusive deals & coupons'],
  },
  merchant: {
    label: 'Merchant', icon: '🏪', color: '#059669',
    subtitle: 'Open your store today',
    bg: 'linear-gradient(135deg, #ECFDF5 0%, #F0FDF4 100%)',
    panelBg: 'linear-gradient(180deg, #059669 0%, #047857 100%)',
    features: ['📦 List unlimited products', '📊 Powerful analytics', '🎫 Create coupons & promotions'],
  },
  affiliate: {
    label: 'Affiliate Partner', icon: '💸', color: '#D97706',
    subtitle: 'Earn commissions by sharing',
    bg: 'linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%)',
    panelBg: 'linear-gradient(180deg, #D97706 0%, #B45309 100%)',
    features: ['🔗 Unique referral links', '💰 5% commission on sales', '📈 Real-time earnings dashboard'],
  },
};

const PortalRegisterPage = () => {
  const { t } = useLanguage();
  const { role } = useParams();
  const config = ROLE_CONFIG[role] || ROLE_CONFIG.customer;
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', password: '', confirmPassword: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirmPassword) return setError('Passwords do not match');
    if (form.password.length < 8) return setError('Password must be at least 8 characters');
    setLoading(true);
    try {
      await authService.register({ ...form, role: role === 'affiliate' ? 'affiliate' : role });
      navigate(role === 'farmer' ? '/merchant/login' : '/login', { state: { registered: true } });
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.errors?.[0]?.message || 'Registration failed');
    }
    setLoading(false);
  };

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  return (
    <div style={{ minHeight: '100vh', display: 'flex', background: config.bg }}>
      {/* Left Branding Panel */}
      <div style={{
        flex: '0 0 400px', background: config.panelBg,
        display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
        padding: '60px 40px', color: '#FFF', position: 'relative', overflow: 'hidden',
      }} className="auth-sidebar d-none d-lg-flex">
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'radial-gradient(circle at 30% 70%, rgba(255,255,255,0.12) 0%, transparent 50%)', pointerEvents: 'none' }} />
        <div style={{ fontSize: '72px', marginBottom: '20px' }}>{config.icon}</div>
        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: '30px', fontWeight: 700, textAlign: 'center', marginBottom: '12px' }}>
          {config.subtitle}
        </h2>
        <div style={{ marginTop: '32px', textAlign: 'left', width: '100%', maxWidth: '280px' }}>
          {config.features.map((f, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px', fontSize: '14px', opacity: 0.9 }}>
              {f}
            </div>
          ))}
        </div>
      </div>

      {/* Right Form */}
      <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px 24px' }}>
        <div style={{ width: '100%', maxWidth: '460px' }}>
          <Link to="/" style={{ textDecoration: 'none', display: 'block', marginBottom: '8px' }}>
            <span style={{ fontSize: '28px', fontWeight: 800, color: '#B91C1C', fontFamily: "'Poppins', sans-serif" }}>
              eBuy<span style={{ color: '#EAB308' }}>Wiz</span>
            </span>
          </Link>
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: '26px', color: '#1F2937', marginBottom: '4px' }}>
            {t('createAccount')} {config.label}
          </h1>
          <p style={{ color: config.color, fontWeight: 600, fontSize: '14px', marginBottom: '24px' }}>{config.subtitle}</p>

          {error && <div style={{ background: '#FEF2F2', color: '#DC2626', padding: '12px 16px', borderRadius: '10px', marginBottom: '16px', fontSize: '14px' }}>{error}</div>}

          <form onSubmit={handleSubmit}>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>{t('firstName')}*</label>
                <input type="text" required style={inputStyle} placeholder="John" value={form.firstName} onChange={set('firstName')} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>{t('lastName')}*</label>
                <input type="text" required style={inputStyle} placeholder="Doe" value={form.lastName} onChange={set('lastName')} />
              </div>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>{t('emailAddress')}*</label>
              <input type="email" required style={inputStyle} placeholder="john@example.com" value={form.email} onChange={set('email')} />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>{t('password')}*</label>
              <div style={{ position: 'relative' }}>
                <input type={showPassword ? 'text' : 'password'} required style={{ ...inputStyle, paddingRight: '44px' }} placeholder="Minimum 8 characters" value={form.password} onChange={set('password')} />
                <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#9CA3AF', fontSize: '18px' }}>
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
            </div>
            <div style={{ marginBottom: '24px' }}>
              <label style={labelStyle}>{t('confirmPassword')}*</label>
              <div style={{ position: 'relative' }}>
                <input type={showPassword ? 'text' : 'password'} required style={{ ...inputStyle, paddingRight: '44px' }} placeholder="Confirm password" value={form.confirmPassword} onChange={set('confirmPassword')} />
                <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#9CA3AF', fontSize: '18px' }}>
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading} style={{ ...btnStyle, background: config.color }}>
              {loading ? t('creatingAccount') : `Create ${config.label} Account`}
            </button>
          </form>

          <p style={{ textAlign: 'center', marginTop: '20px', fontSize: '14px', color: '#6B7280' }}>
            {t('alreadyHaveAccount')}{' '}
            <Link to={role === 'farmer' ? '/merchant/login' : '/login'} style={{ color: config.color, fontWeight: 600, textDecoration: 'none' }}>{t('signInLink')}</Link>
          </p>
          <p style={{ textAlign: 'center', marginTop: '8px', fontSize: '13px', color: '#9CA3AF' }}>
            <Link to="/register" style={{ color: '#9CA3AF', textDecoration: 'none' }}>← {t('chooseDifferentRole')}</Link>
          </p>
        </div>
      </div>

      <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700;800&family=Playfair+Display:ital,wght@0,700;1,700&display=swap" rel="stylesheet" />
    </div>
  );
};

const labelStyle = { display: 'block', fontWeight: 600, fontSize: '13px', color: '#374151', marginBottom: '5px' };
const inputStyle = {
  width: '100%', padding: '13px 16px', border: '2px solid #E5E7EB', borderRadius: '10px',
  fontSize: '14px', outline: 'none', boxSizing: 'border-box', fontFamily: "'Poppins', sans-serif",
};
const btnStyle = {
  width: '100%', padding: '15px', border: 'none', borderRadius: '10px',
  color: '#FFF', fontSize: '15px', fontWeight: 700, cursor: 'pointer',
  fontFamily: "'Poppins', sans-serif", letterSpacing: '0.5px',
};

export default PortalRegisterPage;
